/**
 * EvidenceReviewPanel — runs an evidence-reconciliation review on an
 * EKSELENS360 assessment, powered by Claude Opus 4.8. The deterministic
 * scoring engine is never mutated — this panel only flags where the
 * self-reported scores cannot be defended from the captured evidence and
 * surfaces the artefacts the consultant should chase before SteerCo.
 *
 * Renders inside the admin Assessment "view" page beneath RunPlanPanel.
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import { ShieldCheck, RefreshCw, AlertTriangle, CheckCircle2, HelpCircle, ChevronDown, ChevronUp, FileText } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import GenerationProgress from "./GenerationProgress";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
};

const VERDICT_BADGE = {
  substantiated: { cls: "bg-emerald-50 text-emerald-800 border-emerald-200", icon: CheckCircle2, label: "Substantiated" },
  insufficient:  { cls: "bg-amber-50 text-amber-800 border-amber-200", icon: HelpCircle, label: "Insufficient" },
  contradicted:  { cls: "bg-red-50 text-red-800 border-red-200", icon: AlertTriangle, label: "Contradicted" },
};

const OVERALL_VERDICT = {
  defensible:           { cls: "text-emerald-800 bg-emerald-50",  label: "Defensible" },
  partially_defensible: { cls: "text-amber-800 bg-amber-50",      label: "Partially defensible" },
  not_defensible:       { cls: "text-red-800 bg-red-50",          label: "Not defensible" },
};

const PRIORITY_CLS = {
  high: "text-red-700 bg-red-50",
  med:  "text-amber-700 bg-amber-50",
  low:  "text-zinc-600 bg-zinc-100",
};

function FindingRow({ f }) {
  const meta = VERDICT_BADGE[f.verdict] || VERDICT_BADGE.insufficient;
  const Icon = meta.icon;
  return (
    <li className="border border-zinc-200 bg-white px-3 py-2 text-[12.5px]" data-testid={`evidence-finding-${f.indicator_id}`}>
      <div className="flex items-start gap-2">
        <span className={`inline-flex items-center gap-1 text-[10.5px] font-semibold uppercase tracking-wider px-1.5 py-0.5 border ${meta.cls}`}>
          <Icon size={10}/> {meta.label}
        </span>
        <span className="text-[11px] text-zinc-500 font-mono">{f.indicator_id}</span>
        {typeof f.delta_vs_self === "number" && f.delta_vs_self !== 0 && (
          <span className={`text-[11px] font-semibold ${f.delta_vs_self < 0 ? "text-red-700" : "text-emerald-700"}`}>
            Δ{f.delta_vs_self > 0 ? "+" : ""}{f.delta_vs_self}
          </span>
        )}
      </div>
      <div className="text-zinc-800 mt-1">{f.reasoning}</div>
      {f.evidence_ask && (
        <div className="text-[11.5px] text-zinc-600 mt-1">
          <span className="text-zinc-400">Ask:</span> {f.evidence_ask}
        </div>
      )}
    </li>
  );
}

export default function EvidenceReviewPanel({ runId }) {
  const [review, setReview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [filter, setFilter] = useState("all");
  const [showFindings, setShowFindings] = useState(false);
  const pollRef = useRef(null);
  const toastRef = useRef(null);
  const stopPolling = useCallback(() => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/assessments/runs/${runId}/evidence-review`);
      setReview(data.review);
      if (data.review?.status === "generating") setGenerating(true);
    } catch (e) {
      if (e?.response?.status !== 404) {
        toast.error(e?.response?.data?.detail || "Evidence review load failed");
      }
      setReview(null);
    } finally {
      setLoading(false);
    }
  }, [runId]);

  useEffect(() => { load(); return () => stopPolling(); }, [load, stopPolling]);

  useEffect(() => {
    if (!generating || pollRef.current) return;
    const startedAt = Date.now();
    const MAX_POLL_MS = 9 * 60 * 1000;
    const tick = async () => {
      if (Date.now() - startedAt > MAX_POLL_MS) {
        stopPolling(); setGenerating(false);
        toast.error("This is taking longer than expected. Refresh or regenerate to check.", { id: toastRef.current });
        toastRef.current = null;
        return;
      }
      try {
        const { data } = await api.get(`/admin/assessments/runs/${runId}/evidence-review`);
        setReview(data.review);
        const st = data.review?.status || "ready";
        if (st === "ready") { stopPolling(); setGenerating(false); toast.success("Evidence review ready", { id: toastRef.current }); toastRef.current = null; }
        else if (st === "failed") { stopPolling(); setGenerating(false); toast.error(data.review?.error || "Review failed", { id: toastRef.current }); toastRef.current = null; }
      } catch { /* keep polling */ }
    };
    pollRef.current = setInterval(tick, 4000);
    return () => stopPolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generating, runId]);

  const generate = useCallback(async () => {
    if (generating) return;
    setGenerating(true);
    const ts = toast.loading("Queued — reconciling evidence in the background (~1 min)");
    toastRef.current = ts;
    try {
      const { data } = await api.post(
        `/admin/assessments/runs/${runId}/evidence-review`,
        { force_regenerate: !!review && review.status === "ready" },
      );
      setReview(data.review);
      if (data.cached) { setGenerating(false); toast.success("Loaded from cache", { id: ts }); }
      else if (data.status === "ready") { setGenerating(false); toast.success("Review ready", { id: ts }); }
      else { toast.loading("Generating — polling for completion…", { id: ts }); }
    } catch (e) {
      setGenerating(false);
      toast.error(e?.response?.data?.detail || e.message || "Evidence review failed", { id: ts });
    }
  }, [runId, review, generating]);

  if (loading && !review) return <div className="bg-white border border-zinc-200 p-5 text-zinc-500 text-[13px]">Loading evidence review…</div>;

  const rv = review?.payload;
  const overallMeta = rv?.overall?.verdict ? OVERALL_VERDICT[rv.overall.verdict] : null;
  const findings = rv?.indicator_findings || [];
  const filteredFindings = filter === "all" ? findings : findings.filter((f) => f.verdict === filter);
  const counts = findings.reduce(
    (a, f) => { a[f.verdict] = (a[f.verdict] || 0) + 1; return a; },
    { substantiated: 0, insufficient: 0, contradicted: 0 },
  );

  return (
    <div className="bg-white border border-zinc-200 p-5" data-testid="evidence-review-panel">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="flex items-center gap-2 text-zinc-700 text-[12px] font-medium uppercase tracking-[0.16em]">
            <ShieldCheck size={14}/> Evidence reconciliation
          </div>
          {review && (
            <div className="text-[11px] text-zinc-400 mt-1">
              {review.indicators_reviewed} indicators · {fmtDate(review.generated_at)}
            </div>
          )}
        </div>
      </div>

      <GenerationProgress active={generating} label="Reconciling evidence" typical="~1 min" testid="evidence-generation-progress" />

      <button
        type="button"
        onClick={generate}
        disabled={generating}
        className="w-full bg-[#7737F6] hover:bg-[#5D28C7] disabled:opacity-60 text-white text-[13px] font-medium px-3 py-2 flex items-center justify-center gap-2 transition-colors"
        data-testid="evidence-generate"
      >
        {generating ? <RefreshCw size={14} className="animate-spin"/> : <ShieldCheck size={14}/>}
        {generating ? "Reconciling…" : review ? "Re-run reconciliation" : "Run reconciliation"}
      </button>

      <p className="text-[10.5px] text-zinc-500 mt-2 leading-relaxed">
        Scores are not changed — this is an audit-grade challenge of whether the captured evidence supports the self-reported levels.
      </p>

      {rv && (
        <div className="mt-5 space-y-5">
          <div className="border-l-2 border-[#7737F6] pl-3 space-y-1.5">
            <div className="flex items-center gap-2">
              {overallMeta && (
                <span className={`inline-block text-[10.5px] font-semibold uppercase tracking-wider px-1.5 py-0.5 ${overallMeta.cls}`}>
                  {overallMeta.label}
                </span>
              )}
              <span className="text-[11px] text-zinc-500">
                Credibility: <span className="text-[#7737F6] font-semibold">{rv.overall?.score_credibility_pct ?? "—"}%</span>
              </span>
            </div>
            <p className="text-[13px] text-zinc-800">{rv.overall?.headline}</p>
            {rv.overall?.biggest_credibility_gap && (
              <p className="text-[12.5px] text-amber-800 bg-amber-50 px-2 py-1 mt-1">
                <span className="font-semibold">Biggest gap:</span> {rv.overall.biggest_credibility_gap}
              </p>
            )}
          </div>

          <div className="grid grid-cols-3 gap-2 text-center text-[12px]">
            <button
              type="button"
              onClick={() => { setFilter("substantiated"); setShowFindings(true); }}
              className="bg-emerald-50 border border-emerald-200 p-2 hover:bg-emerald-100"
              data-testid="evidence-filter-substantiated"
            >
              <div className="text-[10.5px] text-emerald-700 uppercase tracking-wider">Substantiated</div>
              <div className="text-[18px] font-medium text-emerald-800 mt-1">{counts.substantiated}</div>
            </button>
            <button
              type="button"
              onClick={() => { setFilter("insufficient"); setShowFindings(true); }}
              className="bg-amber-50 border border-amber-200 p-2 hover:bg-amber-100"
              data-testid="evidence-filter-insufficient"
            >
              <div className="text-[10.5px] text-amber-700 uppercase tracking-wider">Insufficient</div>
              <div className="text-[18px] font-medium text-amber-800 mt-1">{counts.insufficient}</div>
            </button>
            <button
              type="button"
              onClick={() => { setFilter("contradicted"); setShowFindings(true); }}
              className="bg-red-50 border border-red-200 p-2 hover:bg-red-100"
              data-testid="evidence-filter-contradicted"
            >
              <div className="text-[10.5px] text-red-700 uppercase tracking-wider">Contradicted</div>
              <div className="text-[18px] font-medium text-red-800 mt-1">{counts.contradicted}</div>
            </button>
          </div>

          {rv.by_dimension?.length > 0 && (
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-zinc-500 mb-2">By dimension</div>
              <div className="space-y-1.5">
                {rv.by_dimension.map((d) => (
                  <div key={d.dimension_id} className="border border-zinc-200 bg-white px-3 py-2 text-[12.5px]">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-medium text-zinc-800">{d.dimension_id}</span>
                      <span className="text-[#7737F6] font-semibold">{d.credibility_pct ?? "—"}%</span>
                    </div>
                    <div className="text-[11px] text-zinc-500 mt-0.5">
                      {d.substantiated}✓ · {d.insufficient}? · {d.contradicted}✗ across {d.indicators_reviewed} indicators
                    </div>
                    {d.commentary && <div className="text-zinc-700 mt-1">{d.commentary}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {rv.top_evidence_asks?.length > 0 && (
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.12em] text-zinc-500 mb-2">Top evidence asks</div>
              <ul className="space-y-1.5">
                {rv.top_evidence_asks.map((a, i) => (
                  <li key={i} className="border border-zinc-200 bg-white px-3 py-2 text-[12.5px]">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`inline-block text-[10px] font-semibold uppercase px-1.5 py-0.5 ${PRIORITY_CLS[a.priority] || ""}`}>
                        {a.priority}
                      </span>
                      <span className="text-[11px] text-zinc-500 font-mono">{a.dimension_id} · {a.indicator_id}</span>
                    </div>
                    <div className="text-zinc-800">{a.ask}</div>
                    {a.reason && <div className="text-[11.5px] text-zinc-500 mt-1">{a.reason}</div>}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div>
            <button
              type="button"
              onClick={() => setShowFindings((s) => !s)}
              className="text-[11.5px] text-[#7737F6] hover:text-[#5D28C7] flex items-center gap-1"
              data-testid="evidence-toggle-findings"
            >
              {showFindings ? <ChevronUp size={12}/> : <ChevronDown size={12}/>}
              {showFindings ? "Hide" : "Show"} all {findings.length} indicator findings
            </button>
            {showFindings && (
              <div className="mt-2 space-y-2">
                <div className="flex gap-1 text-[11px]">
                  {["all", "substantiated", "insufficient", "contradicted"].map((v) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setFilter(v)}
                      className={`px-2 py-1 border ${filter === v ? "bg-[#7737F6] border-[#7737F6] text-white" : "bg-white border-zinc-300 text-zinc-700 hover:bg-zinc-50"}`}
                      data-testid={`evidence-filter-${v}`}
                    >
                      {v}
                    </button>
                  ))}
                </div>
                <ul className="space-y-1.5">
                  {filteredFindings.map((f) => <FindingRow key={f.indicator_id} f={f} />)}
                </ul>
              </div>
            )}
          </div>

          {rv.executive_narrative && (
            <div className="border border-zinc-300 bg-zinc-50 p-4">
              <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.12em] text-zinc-500 mb-2">
                <FileText size={11}/> SteerCo narrative
              </div>
              <p className="text-[13.5px] text-zinc-800 leading-relaxed">{rv.executive_narrative}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
